// src/utils/whatsappUtils.js
//
// Pure functions for building WhatsApp action plan draft messages.
// No React imports — safe to use anywhere.
//
// Used by:
//   TriageReportModal.js   → ActionPlanTab "Send via WhatsApp" button
//   PreCallBriefingPage.js → future WhatsApp send from briefing
//
// CLINICAL NOTE (v5.7 Agent 06 — WhatsApp Draft Generator):
//   - Navigator ALWAYS reviews before sending — never auto-send
//   - Plain language only — no medical jargon
//   - No catastrophic framing ("serious", "very bad", "could be cancer")
//   - Specific, action-oriented, non-catastrophic, honest about uncertainty
//   - Max 3 high-friction interventions per message (v5.7 rule)
//
// WATI WhatsApp Business number: 7030501008

// ─── Message builder ──────────────────────────────────────────────────────────

/**
 * Builds a WhatsApp-formatted action plan message for the navigator to review.
 *
 * @param {object} patient         - Patient object { firstName, lastName, age, gender }
 * @param {object} triage          - TriageResultDTO.Response
 * @param {Array}  actionItems     - ActionItem[] from buildActionPlan() / deserializeActionPlan()
 * @param {string} drawDate        - Blood draw date string (YYYY-MM-DD or LocalDate)
 * @returns {string}               - WhatsApp-ready message text
 */
export function buildWhatsAppDraft(patient, triage, actionItems, drawDate) {
  const firstName = patient?.firstName || 'there';
  const riskLevel = triage?.consensusRiskLevel || 'UNKNOWN';
  const score     = triage?.consensusRiskScore;
  const date      = drawDate
    ? new Date(drawDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  const lines = [];

  // Greeting
  lines.push(`Hi ${firstName} 👋`);
  lines.push('');

  // Context line
  if (date) {
    lines.push(`Following up on your blood test from ${date}.`);
  } else {
    lines.push('Following up on your recent blood test results.');
  }
  lines.push('');

  // Risk framing — plain language, non-catastrophic per v5.7 escalation rules
  const riskLine = buildRiskLine(riskLevel, score);
  if (riskLine) {
    lines.push(riskLine);
    lines.push('');
  }

  // Action items — P1 first, capped at 3 per v5.7 high-friction rule
  const p1Items = actionItems.filter(a => a.priority === 'P1').slice(0, 3);
  const p2Items = actionItems.filter(a => a.priority === 'P2');
  const p3Items = actionItems.filter(a => a.priority === 'P3');

  const allItems = [...p1Items, ...p2Items, ...p3Items];

  if (allItems.length > 0) {
    lines.push('Here are your next steps:');
    lines.push('');

    allItems.forEach((item, i) => {
      const emoji = priorityEmoji(item.priority);
      lines.push(`${emoji} *${item.title}*`);
      if (item.body && item.body.trim()) {
        lines.push(item.body.trim());
      }
      if (item.window) {
        lines.push(`_Timeline: ${item.window}_`);
      }
      if (i < allItems.length - 1) lines.push('');
    });
  }

  lines.push('');

  // Close
  lines.push(
    'Your navigator will follow up with you in the coming days. ' +
    'Please reach out if you have any questions before then.'
  );
  lines.push('');
  lines.push('— Previu Health Team');

  return lines.join('\n');
}

/**
 * Builds a concise risk summary line using plain, non-catastrophic language.
 * Follows v5.7 prohibited escalation language rules.
 */
function buildRiskLine(riskLevel, score) {
  switch (riskLevel?.toUpperCase()) {
    case 'HIGH':
      return 'Your results show some markers that need attention. ' +
             'We want to work through these with you step by step.';
    case 'MODERATE':
      return 'Your results are in the moderate range — ' +
             'there are some areas we can improve together.';
    case 'LOW':
      return 'Good news — your results are largely in a healthy range. ' +
             'Here are a few things to keep up.';
    default:
      return null;
  }
}

/**
 * Returns a WhatsApp-appropriate emoji for each priority level.
 */
function priorityEmoji(priority) {
  switch (priority?.toUpperCase()) {
    case 'P1': return '🔴';
    case 'P2': return '🟡';
    case 'P3': return '🟢';
    default:   return '•';
  }
}

// ─── Phone number helpers ─────────────────────────────────────────────────────

/**
 * Formats a phone number for WhatsApp deep link.
 * Strips non-digits, adds India country code +91 if 10 digits.
 *
 * @param {string} phone  - Raw phone number from patient record
 * @returns {string|null} - E.164 format without + (e.g. "917030501008") or null
 */
export function formatPhoneForWhatsApp(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return digits;
  if (digits.length === 13 && digits.startsWith('091')) return digits.slice(1);
  return digits.length >= 10 ? digits : null;
}

/**
 * Builds a WhatsApp deep link to open a pre-filled draft in WhatsApp Web / app.
 * The navigator reviews the message before hitting send — it never auto-sends.
 *
 * @param {string} phone   - E.164 phone number (from formatPhoneForWhatsApp)
 * @param {string} message - Draft message text
 * @returns {string}       - wa.me URL
 */
export function buildWhatsAppLink(phone, message) {
  const encoded = encodeURIComponent(message);
  if (phone) {
    return `https://wa.me/${phone}?text=${encoded}`;
  }
  // No phone number — open WhatsApp without a recipient (navigator can paste number)
  return `https://wa.me/?text=${encoded}`;
}

/**
 * Opens the WhatsApp draft link in a new tab.
 * Wrapped in a function so it can be called from onClick handlers.
 *
 * @param {string} phone   - Raw phone from patient record (will be formatted internally)
 * @param {string} message - Draft message text
 */
export function openWhatsAppDraft(phone, message) {
  const formatted = formatPhoneForWhatsApp(phone);
  const link = buildWhatsAppLink(formatted, message);
  window.open(link, '_blank', 'noopener,noreferrer');
}
